import { css } from '@compiled/react';
import React, { useMemo } from 'react';

import { RovoIcon } from '@atlaskit/logo';
import { ButtonItem } from '@atlaskit/menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { AvatarGroupProps, AvatarProps } from '@atlaskit/avatar-group';
import AvatarGroup from '@atlaskit/avatar-group';
import type { ProviderParticipant } from '@atlaskit/collab-provider';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';

import { useAgentsList } from '@atlassian/conversation-assistant-agent';
import type { Agent } from '@atlassian/conversation-assistant-service';

import { AvatarBadge } from './AvatarBadge';
import { generateAvatarDescriptorFromAIProviderParticipant } from './generateAvatarDescriptorFromParticipant';
import { useSessionData } from '@confluence/session-data';

// xcss makes overriding hard
const agentAvatarContainer = css({
	position: 'relative',
	width: '36px',
	height: '36px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .facepileRovoIcon': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > svg': {
			position: 'absolute',
			top: 2,
			left: 2,
			zIndex: 1,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > span': {
			zIndex: 2,
			top: 2,
			left: 2,
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .facepile': {
		width: '36px',
		height: '36px',
		position: 'relative',
		padding: 0,
		margin: 0,
		background: 'transparent',
		border: 0,
		top: -1,

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& > div': {
			width: '36px',
			height: '36px',
			position: 'relative',
			zIndex: 1,

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			div: {
				width: 'initial',
				height: 'initial',
			},
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: {
		width: '36px',
		height: '36px',
	},
});

type OverridesType = NonNullable<AvatarGroupProps['overrides']>;

export function renderAvatar(
	allAgentsFromAssistanceService: Agent[],
	aiProviderAvatarGroupWithData: AvatarProps[],
) {
	return (_component: unknown, props: AvatarProps & { userId?: string }, index: number) => {
		const matchedAgent = allAgentsFromAssistanceService.find((a) => a.id === props.userId);

		return (
			<div
				css={agentAvatarContainer}
				// Ensure the avatars are stacked correctly
				style={{ zIndex: aiProviderAvatarGroupWithData.length - index }}
			>
				{/* eslint-disable-next-line @atlaskit/design-system/no-html-button*/}
				<button
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className="facepile"
					onClick={props?.onClick}
				>
					{matchedAgent?.is_default ? (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						<span className="facepileRovoIcon">
							<RovoIcon {...props} size="large" appearance="brand" />
							<svg
								width="40"
								height="40"
								viewBox="0 0 42 48"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M19.0069 1.14541C20.241 0.436192 21.759 0.436193 22.9931 1.14541L39.9931 10.9152C41.2346 11.6287 42 12.9514 42 14.3833V33.891C42 35.3229 41.2346 36.6456 39.9931 37.3591L22.9931 47.1289C21.759 47.8381 20.241 47.8381 19.0069 47.1289L2.00691 37.3591C0.765414 36.6456 0 35.3229 0 33.891V14.3833C0 12.9514 0.765415 11.6287 2.00691 10.9152L19.0069 1.14541Z"
									fill="#fff"
								/>
							</svg>
						</span>
					) : (
						<AgentAvatar
							{...props}
							key={index}
							agentId={matchedAgent?.id}
							agentIdentityAccountId={matchedAgent?.identity_account_id}
							agentNamedId={matchedAgent?.named_id}
							isForgeAgent={matchedAgent?.creator_type === 'FORGE'}
							forgeAgentIconUrl={matchedAgent?.icon}
							name={props.name}
							size="large"
						/>
					)}
				</button>
				<AvatarBadge name={props.name} sessionId={matchedAgent?.id || ''} />
			</div>
		);
	};
}

type RenderGroupItemType = NonNullable<OverridesType['AvatarGroupItem']>['render'];
type RenderGroupItemParametersType = Parameters<NonNullable<RenderGroupItemType>>;

export function renderAvatarGroupItem(allAgentsFromAssistanceService: Agent[]) {
	return (
		_component: unknown | undefined,
		props: RenderGroupItemParametersType[1] & { avatar: { userId?: string } },
	) => {
		const matchedAgent = allAgentsFromAssistanceService.find((a) => a.id === props.avatar?.userId);

		return (
			<ButtonItem
				ref={undefined}
				iconBefore={
					matchedAgent?.is_default ? (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						<span className="facepileRovoIcon">
							<RovoIcon {...props} size="small" appearance="brand" />
						</span>
					) : (
						<AgentAvatar
							{...props}
							key={matchedAgent?.id}
							agentId={matchedAgent?.id}
							agentIdentityAccountId={matchedAgent?.identity_account_id}
							agentNamedId={matchedAgent?.named_id}
							isForgeAgent={matchedAgent?.creator_type === 'FORGE'}
							forgeAgentIconUrl={matchedAgent?.icon}
							name={matchedAgent?.name}
							size="small"
						/>
					)
				}
			>
				{matchedAgent?.name}
			</ButtonItem>
		);
	};
}

export function useAIProviderAvatarGroup({
	aiProviderParticipants,
}: {
	aiProviderParticipants: ProviderParticipant[];
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const allAgentsFromAssistanceService = useAgentsList({
		assistanceServiceParams: {
			product: 'confluence',
			experienceId: 'ai-mate',
			touchpointSource: 'useAIProviderAvatarGroup',
			siteId: cloudId,
		},
	});

	const aiProviderAvatarGroupWithData = useMemo<AvatarProps[]>(() => {
		return (
			aiProviderParticipants
				.map((participant) => {
					const agentId = participant.userId.replace('agent:', '');
					const matchedAgent = allAgentsFromAssistanceService.find((a) => a.id === agentId);
					const name = matchedAgent?.name || '';

					return {
						...participant,
						name,
						userId: agentId,
						onClick: () => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									source: 'confluence',
									action: 'click',
									actionSubject: 'editorPluginAI',
									actionSubjectId: 'agentAvatarFacepileItem',
									type: 'ui',
									attributes: {
										agentId,
									},
								},
							}).fire();
						},
					};
				})
				.map(generateAvatarDescriptorFromAIProviderParticipant)
				// Dedupe by userId
				.filter((obj1, i, arr) => arr.findIndex((obj2) => obj2.userId === obj1.userId) === i)
		);
	}, [aiProviderParticipants, allAgentsFromAssistanceService, createAnalyticsEvent]);

	const aiProviderAvatarGroup = useMemo(() => {
		if (!aiProviderAvatarGroupWithData.length) {
			return null;
		}

		return (
			<AvatarGroup
				appearance="stack"
				maxCount={3}
				data={aiProviderAvatarGroupWithData}
				testId="editor-in-context-ai-collaborators"
				overrides={{
					Avatar: {
						render: renderAvatar(allAgentsFromAssistanceService, aiProviderAvatarGroupWithData),
					},
					AvatarGroupItem: {
						render: renderAvatarGroupItem(allAgentsFromAssistanceService),
					},
				}}
			/>
		);
	}, [aiProviderAvatarGroupWithData, allAgentsFromAssistanceService]);

	return {
		aiProviderAvatarGroup,
	};
}
